// The SSE frame contracts — every `event:` the four live routes emit, declared once.
//
// They were declared SEVEN times across two apps that cannot import each other:
// the `patch` payload as a plain union in `apps/api/src/routes/streams.ts` and a
// Zod union in `apps/dashboard/src/lib/accountStream.ts`; `run_event` in
// `fleet-events.ts` and `fleetStream.ts`; `audit` in `session-events.ts` and
// `sessionStream.ts`; `ready` as an inline object literal with no type at all;
// `done` as an inline string in three routes; and the queue-standing key as two
// constants whose comment said "mirrors" the other. `packages/domain` is the only
// package BOTH apps depend on, so it is the only place these can live.
//
// The contract is one-way on purpose: each schema here is the shape a consumer
// PARSES, deliberately the more lenient of the two, because a dashboard talks to
// whichever api pod a rolling deploy hands it. So the guard is "everything the
// server emits parses" (`apps/api/src/routes/sse-frames.contract.test.ts`), not a
// type equality that would forbid the leniency the roll needs.

import { z } from 'zod';
import {
  BlockerGuidanceSchema,
  BlockerKindSchema,
  BlockerNeedsSchema,
  BlockerOptionsSchema,
  BlockerResolutionSchema,
} from '../entities/blocker';
import {
  BoxStatsSchema,
  MACHINE_JOBS_VERDICT_STATES,
  type MachineHealth,
  type MachineStatus,
  RunnerOwnerKindSchema,
  RunnerRoleSchema,
  RunnerStatusSchema,
} from '../entities/runner';
import { TaskStatusSchema } from '../entities/task';
import { PlanSummarySchema } from '../value-objects/plan-projection';
import { TaskClassSchema } from '../value-objects/task-envelope';
import { MachineJobRowSchema } from './machine-job';

/**
 * Every `event:` name these routes emit. A frame name is a wire contract — a
 * consumer branches on the literal — so the strings live here rather than being
 * retyped at each `writeSSE` call and each `if (frame.event === …)` branch.
 */
export const SseEvent = {
  /** `/v1/streams` — upsert or remove ONE row under `(topic, key)`. */
  Patch: 'patch',
  /** `/v1/streams` — the negotiated topic set, always the first frame. */
  Ready: 'ready',
  /** `/v1/sessions/:id/events` — one projected `audit_events` row. */
  Audit: 'audit',
  /** `/v1/fleet/runs/:runId/events` — one projected `task_run_events` row. */
  RunEvent: 'run_event',
  /** `/v1/terminal/events/stream` — one projected `TerminalEvent` row. */
  Terminal: 'terminal',
  /** The append log ENDED; nothing can follow it. */
  Done: 'done',
  /** This CONNECTION ended (the duration cap) and the log is still open. */
  Bye: 'bye',
} as const;

export type SseEventName = (typeof SseEvent)[keyof typeof SseEvent];

/**
 * A patched row's body.
 *
 * An OBJECT, never a bare scalar — the half `row: unknown` never said. Every
 * producer is a projection returning an object (`projectTask`, `projectCiRun`,
 * `NotificationResource`, `projectSession`, `projectApproval`, and the synthetic
 * `queueStandingFrame`), and every consumer hands the stored value to a renderer
 * that dereferences fields. So a `row` of `null`, a string or a number used to
 * pass validation and land in the store, surfacing as a crash inside a
 * comparator one navigation later rather than as a dropped frame at the seam it
 * entered. Per-FIELD validation stays the caller's: the shapes are per topic,
 * and this module cannot know which topic a connection asked for.
 */
export const StreamRowBodySchema = z.record(z.string(), z.unknown());

export type StreamRowBody = z.infer<typeof StreamRowBodySchema>;

/** One `patch` payload: upsert a row under `(topic, key)`, or remove it. */
export const StreamPatchSchema = z.union([
  z.object({
    topic: z.string(),
    key: z.string(),
    version: z.string(),
    row: StreamRowBodySchema,
  }),
  z.object({ topic: z.string(), key: z.string(), deleted: z.literal(true) }),
]);

export type StreamPatch = z.infer<typeof StreamPatchSchema>;

/**
 * The `ready` payload — the negotiated topic set, sent as the first frame.
 *
 * It was computed with care (`partitionTopics` exists mostly to produce it) and
 * read by nobody: the client flipped to "live" on `res.ok`, so a token that had
 * lost `read:tasks` got a green pill over a board that could never fill. A
 * consumer that subscribes topics must branch on `subscribed` to know whether
 * ITS topic is being served.
 */
export const StreamReadySchema = z.object({
  /** Registered and authorized — the topics this connection will actually pump. */
  subscribed: z.array(z.string()),
  /** Well-formed but absent from the registry (a typo, or not built yet). */
  ignored: z.array(z.string()),
  /** Registered, but this token lacks the topic's scope. */
  unauthorized: z.array(z.string()),
});

export type StreamReady = z.infer<typeof StreamReadySchema>;

/**
 * The synthetic key the `tasks` topic reserves for the account's full pending
 * depth (#1012) — one O(1) frame per depth change instead of a whole-snapshot
 * re-emit. Its row is {@link QueueStandingRowSchema}, NOT a task row, so a
 * consumer rendering topic rows must exclude this key.
 */
export const QUEUE_STANDING_KEY = 'queue-standing';

/** The depth frame's row shape — narrow on purpose, no per-task fields. */
export const QueueStandingRowSchema = z.object({ depth: z.number().nullable() });

export type QueueStandingRow = z.infer<typeof QueueStandingRowSchema>;

/** The verdict every projected event row carries. */
export const SseOutcomeSchema = z.enum(['success', 'failure', 'skipped']);

/**
 * One `audit` frame — a projected `audit_events` row as
 * `GET /v1/sessions/:id/events` streams it.
 *
 * The three trust-surface fields (#196) are OPTIONAL here while the producer
 * always sends them: a frame that fails the schema is DROPPED, so requiring a
 * field an older pod might omit would blank a whole timeline mid-roll.
 */
export const SessionEventFrameSchema = z.object({
  id: z.string(),
  seq: z.number(),
  tool: z.string(),
  outcome: SseOutcomeSchema,
  created_at_iso: z.string(),
  input_json: z.string(),
  output_json: z.string(),
  reasoning: z.string().nullable().optional(),
  visible_to_maintainer: z.boolean().optional(),
  redacted: z.boolean().optional(),
});

export type SessionEventFrame = z.infer<typeof SessionEventFrameSchema>;

/**
 * One `run_event` frame — a projected `task_run_events` row as
 * `GET /v1/fleet/runs/:runId/events` (and its admin sibling) streams it.
 *
 * `payload_present` defaults rather than being required, for the reason above:
 * absent means "cannot tell", which renders exactly as today.
 */
export const RunEventFrameSchema = z.object({
  id: z.string(),
  seq: z.number(),
  run_id: z.string(),
  task_id: z.string(),
  kind: z.string(),
  outcome: SseOutcomeSchema,
  payload_present: z.boolean().default(true),
  created_at_iso: z.string(),
  payload_json: z.string(),
});

export type RunEventFrame = z.infer<typeof RunEventFrameSchema>;

// ─── `/v1/streams` entity topics: `machines`, `blockers`, `plans` ────────────
//
// One row per ENTITY, keyed by its id, so a client store can patch the entity in
// place or invalidate the REST reads that show it. Each row is the projection the
// entity's own REST read already serves (minus the per-CALLER fields an
// account-wide stream cannot carry — each schema says which), so a widget that
// backfills over REST and patches over SSE sees one shape. `version` is a digest of
// the row (`rowVersion`, `apps/api/src/routes/stream-entity-topics.ts`, whose producers
// are typed to these schemas' output, so a drifted projection fails to compile): a row
// re-emits exactly when something it shows moves.

/** Every {@link MachineStatus}, as the consumer parses it. */
const MACHINE_STATUSES = [
  'unknown',
  'waiting_to_connect',
  'paused',
  'starting',
  'offline',
  'not_responding',
  'working',
  'idle',
] as const satisfies readonly MachineStatus[];

/** Every {@link MachineHealth}, as the consumer parses it. */
const MACHINE_HEALTHS = [
  'unknown',
  'offline',
  'not_responding',
  'overloaded',
  'disk_critical',
  'memory_critical',
  'disk_warning',
  'memory_warning',
  'busy',
  'healthy',
] as const satisfies readonly MachineHealth[];

// Exhaustive both ways: a member added to the domain union and not here fails to
// compile, rather than as a dropped machine row on someone's Fleet page.
type Exhaustive<Union, Listed> = [Union] extends [Listed] ? true : never;
const _statusesExhaustive: Exhaustive<MachineStatus, (typeof MACHINE_STATUSES)[number]> = true;
const _healthsExhaustive: Exhaustive<MachineHealth, (typeof MACHINE_HEALTHS)[number]> = true;
void _statusesExhaustive;
void _healthsExhaustive;

const MachineSignalSchema = z.object({
  level: z.enum(['ok', 'warn', 'crit', 'not_reported']),
  ratio: z.number().nullable(),
});

/**
 * The `machines` topic's row (key = `runners.id`): one machine of
 * `GET /v1/fleet/utilization`'s `boxes[]`, the SAME `toBoxView` projection, so the
 * Fleet cards and the roster can go live off it.
 *
 * WHAT IT DOES NOT CARRY, and why:
 * - `roles` — its narrowing bit depends on the CALLER's `write:prompts`, and the topic
 *   is read once for every subscriber of the account. A role switch is therefore not a
 *   change of this row; the utilization read stays the source of `roles`.
 * - a cross-org row (`via_org` set) — the topic reads the ACCOUNT's machines with no
 *   user (owner ruling v1 G23), so a signed-in person's private machines from their
 *   other orgs are never in it. `via_org` is therefore always `null` here.
 *
 * BEAT FIELDS DO NOT RE-EMIT A ROW. `last_beat_iso`, `stats` and each signal's `ratio`
 * move on every presence beat (`PRESENCE_HEARTBEAT_MS`, 30 s), and the row's `version`
 * is computed WITHOUT them (`machineStateVersion`, `apps/api`), so a frame means a STATE
 * change: presence, enrollment, drain, slots, build, current task, verdict status / health
 * / signal level. Those fields are therefore as fresh as the last state change; a client
 * showing them keeps a slow refresh of its own.
 */
export const MachinesTopicRowSchema = z.object({
  id: z.string(),
  display_name: z.string(),
  hostname: z.string().nullable(),
  owner_kind: RunnerOwnerKindSchema,
  mode: RunnerRoleSchema,
  earmark: z.string().nullable(),
  max_slots: z.number().int(),
  /** `'unknown'`: the presence KV could not be read; the live fields are then not a reading. */
  presence: z.enum(['ok', 'unknown']),
  online: z.boolean(),
  active_sessions: z.number().int().nonnegative(),
  current_class: TaskClassSchema.nullable(),
  stats: BoxStatsSchema.nullable(),
  last_beat_iso: z.string().nullable(),
  runner_version: z.string().nullable(),
  drained_iso: z.string().nullable(),
  specs: z.object({ cpu_cores: z.number(), mem_bytes: z.number() }).nullable(),
  server: z.object({
    key: z.string(),
    label: z.string().nullable(),
    address: z.string().nullable(),
  }),
  enrollment_status: RunnerStatusSchema,
  enrolled_iso: z.string().nullable(),
  first_beat_expected_by: z.string().nullable(),
  owner: z.object({ kind: RunnerOwnerKindSchema, label: z.string().nullable() }),
  via_org: z.string().nullable(),
  /**
   * Every job the machine holds for the account (#6745), each the REST job
   * (`GET /v1/fleet/utilization` `boxes[].jobs`) WITHOUT its `usage`: usage moves on every
   * beat, and a beat field never re-emits a row. Optional so a tab on a newer bundle still
   * accepts a row from a pod that predates the field (the client reads it nullish); the
   * producer (`machineRow`, `apps/api`) always sends it.
   */
  jobs: z.array(MachineJobRowSchema).optional(),
  verdict: z.object({
    status: z.enum(MACHINE_STATUSES),
    health: z.enum(MACHINE_HEALTHS),
    needs_attention: z.boolean(),
    signals: z.object({
      cpu: MachineSignalSchema,
      memory: MachineSignalSchema,
      disk: MachineSignalSchema,
    }),
    /**
     * The worst of the account's jobs on the machine (#6746, `machineJobsVerdict`), null when
     * none counts. ON the version (`machineStateVersion`): `stopping` and `stuck` appear on no
     * job row, so this field is the only one that moves when a job crosses into them.
     * Optional for the `jobs` reason above: a tab on a newer bundle still accepts a row from a
     * pod that predates the field; the producer always sends it.
     */
    jobs_state: z.enum(MACHINE_JOBS_VERDICT_STATES).nullable().optional(),
  }),
});

export type MachinesTopicRow = z.infer<typeof MachinesTopicRowSchema>;

/**
 * The `blockers` topic's row (key = `blockers.id`): one OPEN blocker as
 * `GET /v1/blockers` projects it (`maskBlocker`, `@developerz/mcp`), oldest-asked
 * first. A blocker that closes (answered, withdrawn, expired) leaves the open set, so
 * the topic sends its `{deleted: true}` — read `GET /v1/blockers?open=false` for what
 * happened to it.
 *
 * WHAT IT DOES NOT CARRY: `fix_url`. It is a signed, hour-long WRITE capability minted
 * only for a caller holding `write:secrets` (`fixUrlForCaller`), and this topic is read
 * once for every `read:tasks` subscriber of the account; a fresh mint per read would also
 * move the row's version on every poll. The REST list stays the door to the link. The
 * answer TEXT is absent for the reason `maskBlocker` gives (only `answered` says one
 * exists).
 */
export const BlockersTopicRowSchema = z.object({
  id: z.string(),
  kind: BlockerKindSchema,
  summary: z.string(),
  detail: z.string(),
  repo_full_name: z.string().nullable(),
  run_id: z.string().nullable(),
  task_id: z.string().nullable(),
  runner_id: z.string().nullable(),
  asked_at_iso: z.string(),
  resolution: BlockerResolutionSchema.nullable(),
  answered_at_iso: z.string().nullable(),
  answered_by_user_id: z.string().nullable(),
  // Optional for the roll: a dashboard ahead of the api must not drop every blocker row.
  answered_by_agent: z.string().nullable().optional(),
  answered: z.boolean(),
  raw_task_id: z.string().nullable(),
  needs: BlockerNeedsSchema.nullable(),
  options: BlockerOptionsSchema.nullable(),
  guidance: BlockerGuidanceSchema.nullable(),
  /** Does answering leave an ask a triage judge reads (#6535)? See `BlockerView.answer_files_ask`. */
  answer_files_ask: z.boolean(),
});

export type BlockersTopicRow = z.infer<typeof BlockersTopicRowSchema>;

/**
 * The `plans` topic's row (key = `planId`): one plan of `GET /v1/plans`' first page
 * with no filter — the 100 newest-activity plans — exactly as that list serves it
 * ({@link PlanSummarySchema}, camelCase, as the REST body is). A plan's detail
 * (`GET /v1/plans/:planId`) is not streamed: a moved summary is the signal to re-read
 * it.
 */
export const PlansTopicRowSchema = PlanSummarySchema;

export type PlansTopicRow = z.infer<typeof PlansTopicRowSchema>;

/**
 * The `task_changes` topic's row (key = `tasks.id`, `version` = this `updated_at`): the
 * account's 100 most recently CHANGED tasks, newest first —
 * a SIGNAL, not the task. The `tasks` topic is the 100 newest by CREATION, and most tasks
 * that move are older than that (on the largest account 444 of 542 moves in 24 h,
 * 2026-10-30), so a detail view (`GET /v1/tasks/:id`) learns its task moved from here and
 * re-reads it.
 *
 * `{deleted: true}` on this topic means the task LEFT THE WINDOW (100 newer changes landed),
 * never that the task was deleted: do not drop a cached task on it.
 */
export const TaskChangesTopicRowSchema = z.object({
  id: z.string(),
  status: TaskStatusSchema,
  updated_at: z.string(),
});

export type TaskChangesTopicRow = z.infer<typeof TaskChangesTopicRowSchema>;
